export interface ActivityData {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  image: string;
  duration: string;
  difficulty: string;
  season: string;
  price: string;
  locations: string[];
  requirements: string[];
  included: string[];
  highlights: string[];
  gallery: string[];
  // Optional per-activity UI controls and structured content
  carouselEnabled?: boolean;
  animals?: Array<{
    id: 'wolf' | 'dear';
    image: string;
  }>;
  // Optional path for Creative Commons credits file (relative to site root)
  creditsInfoRelativePath?: string;
}

export const activitiesData: Record<string, ActivityData> = {
  'wild_life_spotting': {
    id: 'wild_life_spotting',
    name: 'Wild Life Spotting',
    subtitle: 'Alla Scoperta della Fauna Selvatica',
    description: 'Vivi l\'emozione di osservare i maestosi cervi nel loro habitat naturale nel Parco Nazionale d\'Abruzzo. Le nostre escursioni guidate ti porteranno nei punti migliori per l\'avvistamento, rispettando sempre la tranquillità degli animali.',
    image: '/activities/wild_life_spotting/wild_life_spotting_homepage.webp',
    duration: '3-6 ore',
    difficulty: 'Principiante',
    season: 'Autunno, Inverno, Primavera',
    price: '€45-95',
    locations: [
      'Parco Nazionale d\'Abruzzo',
      'Pescasseroli',
      'Villetta Barrea',
      'Opi',
      'Civitella Alfedena'
    ],
    requirements: [
      'Età minima: 8 anni',
      'Abiti comodi e caldi',
      'Scarpe da trekking',
      'Binocolo consigliato'
    ],
    included: [
      'Guida naturalistica esperta',
      'Binocolo professionale',
      'Assicurazione',
      'Snack e bevande calde',
      'Materiale informativo'
    ],
    highlights: [
      'Avvistamento di cervi in libertà',
      'Lezioni di etologia',
      'Fotografie naturalistiche',
      'Percorsi panoramici',
      'Educazione ambientale'
    ],
    gallery: [
      '/activities/wild_life_spotting/wild_life_spotting_1_medium.JPG',
      '/activities/wild_life_spotting/wild_life_spotting_2_medium.jpg',
      '/activities/wild_life_spotting/wild_life_spotting_3_medium.jpg'
    ],
    // Disable the carousel for this specific activity while keeping credits visible
    carouselEnabled: false,
    // Structured animals section (content localized separately)
    animals: [
      { id: 'wolf', image: '/activities/wild_life_spotting/wolf.webp' },
      { id: 'dear', image: '/activities/wild_life_spotting/dear.webp' }
    ],
    creditsInfoRelativePath: '/activities/wild_life_spotting/wikimedia.info'
  },
  'cantina_tilli': {
    id: 'cantina_tilli',
    name: 'Azienda Tilli',
    subtitle: 'Degustazione Vini e Prodotti Tipici',
    description: 'Visita l\'Azienda Tilli per una degustazione di vini locali e prodotti tipici dell\'Abruzzo. Un\'esperienza autentica per scoprire i sapori tradizionali della regione.',
    image: '/activities/cantina_tilli/cantina_tilli_homepage.webp',
    duration: '2-3 ore',
    difficulty: 'Principiante',
    season: 'Tutto l\'anno',
    price: '€25-45',
    locations: [
      'Scanno',
      'Villetta Barrea'
    ],
    requirements: [
      'Età minima: 18 anni per degustazione vini',
      'Prenotazione obbligatoria',
      'Abiti casual'
    ],
    included: [
      'Degustazione vini',
      'Prodotti tipici locali',
      'Visita guidata alla cantina',
      'Assicurazione'
    ],
    highlights: [
      'Vini locali di qualità',
      'Prodotti tipici abruzzesi',
      'Visita alla cantina storica',
      'Esperienza autentica',
      'Possibilità di acquisto'
    ],
    gallery: [
      '/activities/cantina_tilli/cantina_tilli_1_medium.webp',
      '/activities/cantina_tilli/cantina_tilli_2_medium.webp',
      '/activities/cantina_tilli/cantina_tilli_3_medium.webp',
      '/activities/cantina_tilli/cantina_tilli_4_medium.webp',
      '/activities/cantina_tilli/cantina_tilli_5_medium.webp'
    ]
  },
  'arrosticini_mucciante': {
    id: 'arrosticini_mucciante',
    name: 'Arrosticini Mucciante',
    subtitle: 'La Tradizione Culinaria Abruzzese',
    description: 'Scopri l\'autentica tradizione culinaria abruzzese con i famosi arrosticini Mucciante. Questa esperienza gastronomica ti porterà alla scoperta di uno dei piatti più iconici della regione, preparato secondo la ricetta tradizionale tramandata di generazione in generazione. Un viaggio nel sapore e nella cultura culinaria abruzzese che non dimenticherai.',
    image: '/activities/arrosticini_mucciante/arrosticini_mucciante_homepage.webp',
    duration: '2-3 ore',
    difficulty: 'Principiante',
    season: 'Tutto l\'anno',
    price: '€30-50',
    locations: [
      'Scanno',
      'Sulmona',
      'L\'Aquila'
    ],
    requirements: [
      'Età minima: 12 anni',
      'Prenotazione obbligatoria',
      'Abiti casual e comodi'
    ],
    included: [
      'Degustazione arrosticini tradizionali',
      'Spiegazione della preparazione',
      'Vino locale di accompagnamento',
      'Pane abruzzese tipico',
      'Assicurazione'
    ],
    highlights: [
      'Arrosticini preparati secondo la tradizione',
      'Spiegazione della tecnica di cottura',
      'Degustazione di vini locali',
      'Immersione nella cultura culinaria abruzzese',
      'Possibilità di acquisto prodotti locali'
    ],
    gallery: [
      '/activities/arrosticini_mucciante/arrosticini_mucciante_1_medium.webp',
      '/activities/arrosticini_mucciante/arrosticini_mucciante_2_medium.webp',
      '/activities/arrosticini_mucciante/arrosticini_mucciante_3_medium.webp'
    ],
    creditsInfoRelativePath: '/activities/arrosticini_mucciante/wikimedia.info'
  },
  'sextantio': {
    id: 'sextantio',
    name: 'Sextantio',
    subtitle: 'L\'Albergo Diffuso del Borgo',
    description: 'Vivi un\'esperienza unica nel suggestivo albergo diffuso Sextantio, situato nel cuore di Santo Stefano di Sessanio. Questo gioiello dell\'ospitalità abruzzese offre la possibilità di soggiornare in case storiche perfettamente restaurate, immerse nell\'atmosfera medievale del borgo. Un\'esperienza autentica tra storia, comfort e tradizione.',
    image: '/activities/sextantio/sextantio_homepage.webp',
    duration: '1-3 giorni',
    difficulty: 'Principiante',
    season: 'Tutto l\'anno',
    price: '€150-300 per notte',
    locations: [
      'Santo Stefano di Sessanio',
      'Gran Sasso'
    ],
    requirements: [
      'Prenotazione obbligatoria',
      'Documento d\'identità valido',
      'Abiti comodi per esplorare il borgo'
    ],
    included: [
      'Pernottamento in casa storica restaurata',
      'Colazione tipica abruzzese',
      'Visita guidata del borgo medievale',
      'Accesso alle strutture comuni',
      'WiFi e servizi moderni'
    ],
    highlights: [
      'Soggiorno in case storiche autentiche',
      'Atmosfera medievale preservata',
      'Viste panoramiche mozzafiato',
      'Cucina tradizionale abruzzese',
      'Esperienza di turismo lento e sostenibile'
    ],
    gallery: [
      '/activities/sextantio/sextantio_1_medium.webp',
      '/activities/sextantio/sextantio_2_medium.webp',
      '/activities/sextantio/sextantio_3_medium.webp',
      '/activities/sextantio/sextantio_4_medium.webp',
      '/activities/sextantio/sextantio_5_medium.webp',
      '/activities/sextantio/sextantio_6_medium.webp',
      '/activities/sextantio/sextantio_7_medium.webp',
      '/activities/sextantio/sextantio_8_medium.webp',
      '/activities/sextantio/sextantio_9_medium.webp',
      '/activities/sextantio/sextantio_10_medium.webp',
      '/activities/sextantio/sextantio_11_medium.webp',
      '/activities/sextantio/sextantio_12_medium.webp',
      '/activities/sextantio/sextantio_13_medium.webp',
      '/activities/sextantio/sextantio_14_medium.webp',
      '/activities/sextantio/sextantio_15_medium.webp',
      '/activities/sextantio/sextantio_16_medium.webp',
      '/activities/sextantio/sextantio_17_medium.webp',
      '/activities/sextantio/sextantio_18_medium.webp',
      '/activities/sextantio/sextantio_19_medium.webp',
      '/activities/sextantio/sextantio_20_medium.webp',
      '/activities/sextantio/sextantio_21_medium.webp',
      '/activities/sextantio/sextantio_22_medium.webp',
      '/activities/sextantio/sextantio_23_medium.webp',
      '/activities/sextantio/sextantio_24_medium.webp',
      '/activities/sextantio/sextantio_25_medium.webp',
      '/activities/sextantio/sextantio_26_medium.webp',
      '/activities/sextantio/sextantio_27_medium.webp',
      '/activities/sextantio/sextantio_28_medium.webp',
      '/activities/sextantio/sextantio_29_medium.webp',
      '/activities/sextantio/sextantio_30_medium.webp',
      '/activities/sextantio/sextantio_31_medium.webp',
      '/activities/sextantio/sextantio_32_medium.webp',
      '/activities/sextantio/sextantio_33_medium.webp'
    ]
  },
};

export const getActivityData = (activityId: string): ActivityData | null => {
  return activitiesData[activityId] || null;
}; 